<template>
  <div class="max-w-7xl mx-auto space-y-5">
    <!-- 页面标题（仅有查看权限时显示） -->
    <div v-if="canViewList" class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl text-gray-900">我的成果</h2>
      </div>
      <div class="text-sm text-gray-500">
        共 {{ total }} 项成果
      </div>
    </div>

    <!-- 搜索和筛选区域 -->
    <div v-if="canViewList" class="card p-6 mb-6">
      <div class="flex items-center space-x-2">
        <!-- 搜索框 -->
        <div class="w-48">
          <el-input
            v-model="searchTerm"
            size="large"
            placeholder="搜索成果..."
            :prefix-icon="Search"
            clearable
          />
        </div>
        
        <!-- 类型筛选 -->
        <div class="flex items-center space-x-1">
          <span class="text-sm text-gray-600 whitespace-nowrap">类型：</span>
          <el-select v-model="selectedType" size="large" class="w-24" popper-class="dm-select-popper">
            <el-option label="全部" value="全部" />
            <el-option label="教研项目" value="教研项目" />
            <el-option label="科研项目" value="科研项目" />
            <el-option label="学术会议" value="学术会议" />
            <el-option label="论文" value="论文" />
            <el-option label="专利" value="专利" />
            <el-option label="软著" value="软著" />
            <el-option label="专著" value="专著" />
            <el-option label="荣誉获奖" value="荣誉获奖" />
            <el-option label="其他" value="其他" />
          </el-select>
        </div>

        <!-- 归属筛选 -->
        <div class="flex items-center space-x-1">
          <span class="text-sm text-gray-600 whitespace-nowrap">归属：</span>
          <el-select v-model="selectedAchievementType" size="large" class="w-24" popper-class="dm-select-popper">
            <el-option label="全部" value="全部" />
            <el-option label="单人" value="单人成果" />
            <el-option label="多人" value="多人成果" />
          </el-select>
        </div>

        <!-- 状态筛选 -->
        <div class="flex items-center space-x-1">
          <span class="text-sm text-gray-600 whitespace-nowrap">状态：</span>
          <el-select v-model="selectedStatus" size="large" class="w-24" popper-class="dm-select-popper">
            <el-option label="全部" value="全部" />
            <el-option label="草稿" value="草稿" />
            <el-option label="已提交" value="已提交" />
          </el-select>
        </div>

        <!-- 时间筛选 -->
        <div class="flex items-center space-x-1">
          <span class="text-sm text-gray-600 whitespace-nowrap">时间：</span>
          <el-select v-model="selectedTimeRange" size="large" class="w-20" popper-class="dm-select-popper">
            <el-option label="全部" value="全部" />
            <el-option label="半年" value="halfYear" />
            <el-option label="一年" value="oneYear" />
            <el-option label="三年" value="threeYears" />
            <el-option label="五年" value="fiveYears" />
          </el-select>
        </div>

        <!-- 排序选择 -->
        <div class="flex items-center space-x-1">
          <span class="text-sm text-gray-600 whitespace-nowrap">排序：</span>
          <el-select v-model="sortField" size="large" class="w-20" popper-class="dm-select-popper">
            <el-option label="提交" value="submitTime" />
            <el-option label="更新" value="updateTime" />
            <el-option label="类型" value="type" />
            <el-option label="状态" value="status" />
          </el-select>
          <el-select v-model="sortOrder" size="large" class="w-20" popper-class="dm-select-popper">
            <el-option label="降序" value="desc" />
            <el-option label="升序" value="asc" />
          </el-select>
        </div>
      </div>
    </div>

    <!-- 成果列表 -->
    <div v-if="canViewList" v-loading="loading" class="space-y-4" element-loading-text="加载中...">
      <el-card v-if="!loading && paginatedAchievements.length === 0" class="p-12 text-center">
        <h3 class="text-gray-900 mb-2">暂无匹配的成果</h3>
        <p class="text-gray-600">
          {{ searchTerm || selectedType !== "全部" || selectedStatus !== "全部" || selectedAchievementType !== "全部" || selectedTimeRange !== "全部"
            ? "请尝试调整搜索条件或筛选条件" 
            : "您还没有提交任何成果，开始创建您的第一个成果吧"
          }}
        </p>
      </el-card>
      
      <div
        v-for="achievement in paginatedAchievements"
        :key="achievement.id"
        class="card p-5 hover:shadow-md transition-shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="mb-3">
              <!-- 第一行：类型标签 + 成果名称 + 归属标签（单人/多人）+ 状态标签 -->
              <div class="flex items-center space-x-3 mb-1">
                <span class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 border border-gray-200">
                  {{ achievement.type }}
                </span>
                <span class="text-base text-gray-900 font-[550]">{{ achievement.title }}</span>
                <el-tag size="small" effect="plain" :class="achievement.achievementType === 'individual' ? 'badge-outline-blue' : 'badge-outline-purple'">
                  {{ achievement.achievementType === 'individual' ? '单人成果' : '多人成果' }}
                </el-tag>
                <el-tag size="small" effect="plain" :class="achievement.status === '已提交' ? 'badge-outline-green' : 'badge-outline-gray'">
                  {{ achievement.status }}
                </el-tag>
              </div>
              <!-- 第二行：只显示提交时间和更新时间 -->
              <div class="flex items-center space-x-4 text-sm text-gray-600 flex-wrap">
                <span>提交：{{ achievement.submitTime }}</span>
                <span>更新：{{ achievement.updateTime || '无' }}</span>
                <span v-if="achievement.achievementType === 'team' && achievement.teamMembers && achievement.teamMembers.length" class="text-purple-600">
                  团队成员：{{ achievement.teamMembers.length }}人
                </span>
              </div>
              <!-- 第三行：备注信息 -->
              <p v-if="achievement.remark" class="text-gray-600 text-sm mt-2 line-clamp-1">
                {{ achievement.remark }}
              </p>
            </div>
          </div>
          <!-- 右侧按钮：查看、编辑、删除 -->
          <div class="flex items-center gap-2 ml-4">
            <el-button class="action-btn" plain @click="viewAchievement(achievement)">
              <el-icon class="mr-1"><View /></el-icon>查看
            </el-button>
            <el-button v-if="canEditSubmitted(achievement)" class="action-btn action-edit" plain @click="editAchievement(achievement)">
              <el-icon class="mr-1"><Edit /></el-icon>编辑
            </el-button>
            <el-button v-if="canDeleteSubmitted(achievement)" class="action-btn action-delete" plain @click="deleteAchievement(achievement)">
              <el-icon class="mr-1"><Delete /></el-icon>删除
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 无权限/未开放提示 -->
    <el-card v-else class="p-12 text-center">
      <h3 class="text-gray-900 mb-2">当前未开放时间段</h3>
      <p class="text-gray-600 text-sm">请联系管理员或在开放期间再来查看我的成果</p>
    </el-card>

    <!-- 分页组件（仅有查看权限时显示） -->
    <div v-if="canViewList && totalPages > 1" class="flex justify-center">
      <nav role="navigation" aria-label="pagination" class="dm-pagination mx-auto flex w-full justify-center">
        <ul class="flex flex-row items-center gap-1">
          <!-- Previous -->
          <li>
            <button
              @click="goToPrevious"
              :disabled="currentPage === 1"
              class="inline-flex items-center justify-center gap-1 h-9 rounded-md text-sm font-medium transition-all px-2.5 sm:pl-2.5 disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <ChevronLeft class="size-4" />
              <span class="hidden sm:block">Previous</span>
            </button>
          </li>

          <!-- 中间页码与省略号 -->
          <template v-for="pageNum in Array.from({length: totalPages}, (_, i) => i + 1)" :key="pageNum">
            <!-- 显示逻辑：一直显示第1页、最后一页、当前页及其前后各一页 -->
            <template v-if="pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)">
              <li>
                <button
                  @click="goToPage(pageNum)"
                  :aria-current="currentPage === pageNum ? 'page' : undefined"
                  :class="[
                    'inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                    currentPage === pageNum
                      ? 'border bg-white text-gray-900'
                      : 'text-gray-900'
                  ]"
                >
                  {{ pageNum }}
                </button>
              </li>
            </template>
            <template v-else-if="pageNum === currentPage - 2 || pageNum === currentPage + 2">
              <li>
                <span class="flex w-9 h-9 items-center justify-center">
                  <MoreHorizontal class="size-4" />
                  <span class="sr-only">More pages</span>
                </span>
              </li>
            </template>
          </template>

          <!-- Next -->
          <li>
            <button
              @click="goToNext"
              :disabled="currentPage === totalPages"
              class="inline-flex items-center justify-center gap-1 h-9 rounded-md text-sm font-medium transition-all px-2.5 sm:pr-2.5 disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <span class="hidden sm:block">Next</span>
              <ChevronRight class="size-4" />
            </button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- 详情对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="selectedAchievement ? selectedAchievement.title : ''"
      width="70%"
      :append-to-body="false"
      destroy-on-close
    >
      <div v-if="selectedAchievement" class="space-y-6">
        <!-- 基本信息 -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <span class="text-sm text-gray-600">成果类型</span>
            <p class="text-gray-900 mt-1">{{ selectedAchievement.type }}</p>
          </div>
          <div>
            <span class="text-sm text-gray-600">归属类型</span>
            <div class="mt-1">
              <el-tag size="small" effect="plain" :class="selectedAchievement.achievementType === 'individual' ? 'badge-outline-blue' : 'badge-outline-purple'">
                {{ selectedAchievement.achievementType === 'individual' ? '单人成果' : '多人成果' }}
              </el-tag>
            </div>
          </div>
          <div>
            <span class="text-sm text-gray-600">状态</span>
            <div class="mt-1">
              <el-tag size="small" effect="plain" :class="selectedAchievement.status === '已提交' ? 'badge-outline-green' : 'badge-outline-gray'">
                {{ selectedAchievement.status }}
              </el-tag>
            </div>
          </div>
          <div>
            <span class="text-sm text-gray-600">提交时间</span>
            <p class="text-gray-900 mt-1">{{ selectedAchievement.submitTime }}</p>
          </div>
          <div>
            <span class="text-sm text-gray-600">更新时间</span>
            <p class="text-gray-900 mt-1">{{ selectedAchievement.updateTime }}</p>
          </div>
        </div>

        <!-- 团队成员信息 -->
        <div v-if="selectedAchievement.achievementType === 'team' && selectedAchievement.teamMembers && selectedAchievement.teamMembers.length > 0">
          <div class="text-sm text-gray-600 mb-2">团队成员信息</div>
          <div class="overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-50">
                  <th class="border border-gray-200 px-4 py-2 text-left text-sm text-gray-900 font-semibold">位次</th>
                  <th class="border border-gray-200 px-4 py-2 text-left text-sm text-gray-900 font-semibold">姓名</th>
                  <th class="border border-gray-200 px-4 py-2 text-left text-sm text-gray-900 font-semibold">身份</th>
                  <th class="border border-gray-200 px-4 py-2 text-left text-sm text-gray-900 font-semibold">所属单位</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(member) in selectedAchievement.teamMembers" :key="member.id">
                  <td class="border border-gray-200 px-4 py-2 text-sm text-gray-900">{{ member.rank }}</td>
                  <td class="border border-gray-200 px-4 py-2 text-sm text-gray-900">{{ member.name }}</td>
                  <td class="border border-gray-200 px-4 py-2 text-sm text-gray-900">
                    {{ member.identity === 'teacher' ? '教师' : member.identity === 'student' ? '学生' : '社会人士' }}
                  </td>
                  <td class="border border-gray-200 px-4 py-2 text-sm text-gray-900">{{ member.unit }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 备注信息 -->
        <div v-if="selectedAchievement.remark">
          <div class="text-sm text-gray-600">备注</div>
          <p class="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{{ selectedAchievement.remark }}</p>
        </div>

        <!-- 佐证材料 -->
        <div v-if="selectedAchievement.evidence && selectedAchievement.evidence.length > 0">
          <div class="text-sm text-gray-600">佐证材料</div>
          <div class="mt-2 space-y-2">
            <div
              v-for="(file, index) in selectedAchievement.evidence"
              :key="index"
              class="flex items-center space-x-2 p-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100"
            >
              <el-icon class="text-blue-500"><Document /></el-icon>
              <span class="text-blue-600 text-sm">{{ file }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores'
import { Search, View, Edit, Delete, Document } from '@element-plus/icons-vue'
import { myAchievementPage, /* achievementDetail, */ deleteAchievement as deleteAchievementAPI } from '@/api/achievement'
import { getPatent } from '@/api/patent'
import { getPaperById as getPaper } from '@/api/paper'
import { getSoftware } from '@/api/software'
import { getMonograph } from '@/api/monograph'
import { getHonor } from '@/api/honor'
import { getOther } from '@/api/other'
import { getAcademicConferenceById } from '@/api/academicConference'
import { getTeachingProject } from '@/api/teachingResearchProject'
import { getScientificResearchProject } from '@/api/scientificResearchProject'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-vue-next'
import { ElMessage, ElMessageBox } from 'element-plus'

const props = defineProps({
  onEdit: Function
})

// 路由与全局状态（用于未传入 onEdit 时的回退处理）
const router = useRouter()
const store = useAppStore()
const { setActiveSection, setActiveTab } = store

// 响应式数据
const searchTerm = ref("")
const selectedType = ref("全部")
const selectedAchievementType = ref("全部")
const selectedStatus = ref("全部")
const selectedTimeRange = ref("全部")
const sortField = ref("submitTime")
const sortOrder = ref("desc")
const currentPage = ref(1)
const itemsPerPage = 5
const dialogVisible = ref(false)
const selectedAchievement = ref(null)
const loading = ref(false)

// 全量成果数据（仅在首次或手动刷新从后端获取一次）
const achievements = ref([])

const canViewList = computed(() => store.hasFunctionByName('查看成果列表'))
const canEditSubmitted = (a) => a?.status !== '已提交' || store.hasFunctionByName('编辑已提交成果')
const canDeleteSubmitted = (a) => a?.status !== '已提交' || store.hasFunctionByName('删除已提交成果')

// ========== 类型映射 ==========

// 中文类型 -> 英文类型（API调用）
// 已统一使用前端枚举，不再向后端发送类型枚举

// 英文类型 -> 中文类型（显示）
const typeMapToCn = {
  'teachingResearchProject': '教研项目',
  'scientificResearchProject': '科研项目',
  'paper': '论文',
  'patent': '专利',
  'softwareCopyright': '软著',
  'monograph': '专著',
  'academicConference': '学术会议',
  'honoraryAward': '荣誉获奖',
  'otherAchievement': '其他'
}

// ========== 数据转换函数 ==========

// 将后端返回的成果数据转换为前端显示格式
function transformAchievementData(item) {
  const getAny = (obj, keys) => {
    for (const k of keys) {
      const v = obj?.[k]
      if (v != null && v !== '') return v
    }
    return ''
  }
  const typeCodeToEn = {
    '1': 'teachingResearchProject',
    '2': 'scientificResearchProject',
    '3': 'academicConference',
    '4': 'paper',
    '5': 'patent',
    '6': 'softwareCopyright',
    '7': 'monograph',
    '8': 'honoraryAward',
    '9': 'otherAchievement'
  }
  const rawType = item.type
  const rawTypeStr = typeof rawType === 'number' ? String(rawType) : String(rawType || '')
  const rawTypeNormalized = typeCodeToEn[rawTypeStr] || rawTypeStr
  let evidence = []
  if (item.evidenceMaterial) {
    try {
      evidence = typeof item.evidenceMaterial === 'string' 
        ? JSON.parse(item.evidenceMaterial) 
        : item.evidenceMaterial
    } catch {
      evidence = [item.evidenceMaterial]
    }
  }

  const transformed = {
    id: item.id,  // 汇总表ID (115)
    detailId: item.detailId || item.detail_id || item.id,  // 详情表ID (11) ← 关键！
    title: getAny(item, ['name','projectName','paperName','patentName','softwareName','monographName','awardName','conferenceName','project_result_name','title']),
    type: typeMapToCn[rawTypeNormalized] || item.type,
    rawType: rawType,
    rawTypeNormalized,
    // achievementType: 1=个人, 2=团队（优先使用achievementType，其次teamType）
    achievementType: (() => {
      const at = item.achievementType ?? item.teamType
      if (at === 1) return 'individual'
      if (at === 2) return 'team'
      return 'individual'  // 默认单人
    })(),
    teamMembers: (item.memberList || []).map(m => ({
      id: m.id || m.position,
      name: m.name,
      rank: m.position,
      identity: m.identity,
      unit: m.unit
    })),
    // 备注字段：优先description，其次remark
    remark: getAny(item, ['description','remark','remarks','note']),
    evidence: evidence,
    // status: 1=草稿, 2=已提交（根据实际数据判断）
    status: item.status === 1 ? '草稿' : '已提交',
    // 时间字段：实际字段名是createdTime和updatedTime
    submitTime: getAny(item, ['createdTime','createTime','create_time','submitTime','submit_time']),
    updateTime: getAny(item, ['updatedTime','updateTime','update_time'])
  }

  return transformed
}

// ========== API 调用方法 ==========

// 获取成果列表
async function fetchAchievements() {
  try {
    loading.value = true
    // 全量拉取：分页循环直至拿完所有记录
    const PAGE_LIMIT = 100
    const MAX_PAGES = 1000
    const merged = []
    for (let pageIndex = 1; pageIndex <= MAX_PAGES; pageIndex++) {
      const res = await myAchievementPage({ page: pageIndex, pageSize: PAGE_LIMIT })
      const payload = res?.data ?? res
      const batch = Array.isArray(payload?.records) ? payload.records : []
      merged.push(...batch)
      const totalCount = Number(payload?.total ?? merged.length)
      if (batch.length < PAGE_LIMIT || merged.length >= totalCount) break
    }
    achievements.value = merged.map(transformAchievementData)
  } catch (error) {
    console.error('获取成果列表失败:', error)
    ElMessage.error('获取成果列表失败，请稍后重试')
    achievements.value = []
  } finally {
    loading.value = false
  }
}

// 前端筛选后的数据与分页
const filteredAchievements = computed(() => applyClientFiltersAndSort(achievements.value || []))
const paginatedAchievements = computed(() => {
  const list = filteredAchievements.value || []
  const start = (currentPage.value - 1) * itemsPerPage
  return list.slice(start, start + itemsPerPage)
})
const total = computed(() => (filteredAchievements.value || []).length)
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / itemsPerPage)))

// 方法
const viewAchievement = async (achievement) => {
  try {
    loading.value = true
    
    // 先使用列表数据打开对话框
    selectedAchievement.value = { ...achievement }
    dialogVisible.value = true
    
    // ========== 保留原detail接口调用（但不使用返回数据） ==========
    // try {
    //   const queryId = achievement.id
    //   const queryType = achievement.rawTypeNormalized || achievement.rawType
    //   const res = await achievementDetail({ type: queryType, id: queryId })
    //   console.log(' detail接口响应（仅供参考）:', res)
    // } catch (e) {
    //   console.warn('detail接口调用失败:', e)
    // }
    // ================================================================
    
    // 直接用 detailId 调用专用详情接口获取完整数据
    const detailId = achievement.detailId
    
    if (detailId) {
      const fullData = await getSpecificDetail(achievement.type, detailId)
      
      if (fullData) {
        const transformedFull = transformAchievementData(fullData)
        
        // 智能合并：优先使用列表数据的关键字段，用详情数据补充
        selectedAchievement.value = {
          ...achievement,  // 列表数据作为基础
          // 只用详情数据补充团队成员和佐证材料
          teamMembers: transformedFull.teamMembers?.length > 0 
            ? transformedFull.teamMembers 
            : achievement.teamMembers,
          evidence: transformedFull.evidence?.length > 0 
            ? transformedFull.evidence 
            : achievement.evidence,
          remark: transformedFull.remark || achievement.remark,
          // 确保关键字段不被覆盖
          id: achievement.id,
          detailId: achievement.detailId,
          type: achievement.type,
          achievementType: achievement.achievementType,  // 保留列表的归属类型
          status: achievement.status,  // 保留列表的状态
          submitTime: achievement.submitTime,  // 保留列表的提交时间
          updateTime: achievement.updateTime   // 保留列表的更新时间
        }
      } else {
        console.warn(' viewAchievement - 专用接口调用失败，只使用列表数据')
      }
    } else {
      console.warn(' viewAchievement - 没有detailId，只使用列表数据')
    }
  } catch (error) {
    console.error('查看成果失败:', error)
    ElMessage.error('查看成果失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

// 根据成果类型调用对应的专用详情接口
async function getSpecificDetail(type, detailId) {
  try {
    const apiMap = {
      '专利': getPatent,
      '论文': getPaper,
      '软著': getSoftware,
      '专著': getMonograph,
      '荣誉获奖': getHonor,
      '其他': getOther,
      '学术会议': getAcademicConferenceById,
      '教研项目': getTeachingProject,
      '科研项目': getScientificResearchProject
    }
    const apiFunc = apiMap[type]
    if (!apiFunc) {
      console.warn(' getSpecificDetail - 未找到对应的API函数')
      console.warn('  - 查找的类型:', type)
      console.warn('  - 可用的类型:', Object.keys(apiMap).join(', '))
      return null
    }
    const res = await apiFunc(detailId)
    const data = res?.data ?? res
    return data
  } catch (error) {
    console.error(' getSpecificDetail - 调用失败:', error)
    return null
  }
}

const editAchievement = async (achievement) => {
  const typeMap = {
    '教研项目': 'teaching-project',
    '科研项目': 'research-project',
    '学术会议': 'conference',
    '论文': 'paper',
    '专利': 'patent',
    '软著': 'software',
    '专著': 'monograph',
    '荣誉获奖': 'award',
    '其他': 'other'
  }
  const tab = typeMap[achievement.type] || 'other'
  
  // 直接使用 detailId（详情表ID），不是 id（汇总表ID）
  const detailId = achievement.detailId || achievement.id
  
  
  if (typeof props.onEdit === 'function') {
    props.onEdit(tab, detailId)
  } else {
    try { setActiveSection('report'); setActiveTab(tab) } catch { /* ignore */ }
    
    const routeParams = { 
      name: 'ReportForms', 
      query: { 
        detailId: String(detailId),  // 详情表ID（用于查询详情）
        id: String(achievement.id),  // 汇总表ID（保留兼容性）
        type: tab 
      }
    }
    router.push(routeParams)
  }
}

const deleteAchievement = async (achievement) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这个成果吗？删除后无法恢复。',
      '确认删除',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        customClass: 'custom-message-box',
        confirmButtonClass: 'custom-confirm-button',
        cancelButtonClass: 'custom-cancel-button'
      }
    )
    
    loading.value = true
    
    // 关键修正：使用 detailId（详情表ID）而不是 id（汇总表ID）
    const detailId = achievement.detailId || achievement.id
    const deleteParams = {
      type: achievement.rawTypeNormalized || achievement.rawType,
      id: detailId  // 后端需要的是详情表ID
    }
    
    await deleteAchievementAPI(deleteParams)
    
    // 无论返回什么，都认为删除成功并刷新
    ElMessage.success('删除成功')
    
    // 重置到第一页并刷新数据
    currentPage.value = 1
    await fetchAchievements()
    
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除成果失败:', error)
      ElMessage.error('删除失败，请稍后重试')
    }
  } finally {
    loading.value = false
  }
}

// 分页方法
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const goToPrevious = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const goToNext = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

// 用于避免重复调用的标志
let skipNextPageWatch = false

// 监听筛选条件和排序变化，重置页码并重新获取数据
watch(
  [searchTerm, selectedType, selectedAchievementType, selectedStatus, selectedTimeRange, sortField, sortOrder],
  () => {
    skipNextPageWatch = true
    currentPage.value = 1
  }
)

// 兜底过滤与排序（前端）
function applyClientFiltersAndSort(list) {
  let result = Array.isArray(list) ? [...list] : []

  // 关键词搜索（标题）
  if (searchTerm.value) {
    const q = String(searchTerm.value).trim().toLowerCase()
    result = result.filter(a => String(a.title || '').toLowerCase().includes(q))
  }

  if (selectedType.value !== '全部') {
    const target = selectedType.value
    result = result.filter(a => a.type === target)
  }

  if (selectedAchievementType.value !== '全部') {
    const target = selectedAchievementType.value === '单人成果' ? 'individual' : 'team'
    result = result.filter(a => a.achievementType === target)
  }

  if (selectedStatus.value !== '全部') {
    result = result.filter(a => a.status === selectedStatus.value)
  }

  if (selectedTimeRange.value !== '全部') {
    const now = new Date()
    const addMonths = (d, m) => new Date(d.getFullYear(), d.getMonth() - m, d.getDate())
    const start = {
      halfYear: addMonths(now, 6),
      oneYear: addMonths(now, 12),
      threeYears: addMonths(now, 36),
      fiveYears: addMonths(now, 60)
    }[selectedTimeRange.value]
    result = result.filter(a => {
      const t = a.submitTime ? new Date(a.submitTime.replace(/-/g, '/')) : null
      return t && start && t >= start
    })
  }

  const order = sortOrder.value === 'asc' ? 1 : -1
  const byTypeIndex = (t) => {
    const orderList = ['教研项目','科研项目','学术会议','论文','专利','软著','专著','荣誉获奖','其他']
    const i = orderList.indexOf(t)
    return i === -1 ? 999 : i
  }
  const cmp = {
    submitTime: (a, b) => {
      const ta = a.submitTime ? new Date(a.submitTime.replace(/-/g, '/')).getTime() : 0
      const tb = b.submitTime ? new Date(b.submitTime.replace(/-/g, '/')).getTime() : 0
      return (ta - tb) * order
    },
    updateTime: (a, b) => {
      const ta = a.updateTime ? new Date(a.updateTime.replace(/-/g, '/')).getTime() : 0
      const tb = b.updateTime ? new Date(b.updateTime.replace(/-/g, '/')).getTime() : 0
      return (ta - tb) * order
    },
    type: (a, b) => (byTypeIndex(a.type) - byTypeIndex(b.type)) * order,
    status: (a, b) => {
      const sa = a.status === '草稿' ? 0 : 1
      const sb = b.status === '草稿' ? 0 : 1
      return (sa - sb) * order
    }
  }[sortField.value]

  if (cmp) result.sort(cmp)
  return result
}

// 监听页码变化，重新获取数据
watch(
  currentPage,
  () => {
    if (skipNextPageWatch) {
      skipNextPageWatch = false
      return
    }
  }
)

// 组件挂载时获取数据
onMounted(() => {
  fetchAchievements()
})
</script>

<style scoped>
/* 卡片样式 */
.card {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background-color: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* badge样式 */
.badge-outline-blue {
  color: #2563eb;
  border: 1px solid #bfdbfe;
  background-color: #ffffff;
}

.badge-outline-purple {
  color: #7c3aed;
  border: 1px solid #d8b4fe;
  background-color: #ffffff;
}

.badge-outline-green {
  color: #059669;
  border: 1px solid #a7f3d0;
  background-color: #ffffff;
}

.badge-outline-gray {
  color: #6b7280;
  border: 1px solid #d1d5db;
  background-color: #ffffff;
}

/* 按钮样式（对齐DepartmentManagement） */
.el-button.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background-color: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.el-button.action-btn:hover {
  background-color: #f9fafb;
}

.el-button.action-edit {
  color: #2563eb;
  border-color: #bfdbfe;
}

.el-button.action-edit:hover {
  border-color: #93c5fd;
}

.el-button.action-delete {
  color: #dc2626;
  border-color: #fecaca;
}

.el-button.action-delete:hover {
  border-color: #fca5a5;
}

/* 分页按钮悬停统一为深灰底色 */
.dm-pagination button:hover {
  background-color: #e5e7eb;
}

/* 下拉框选项样式 */
:deep(.dm-select-popper) {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  background-color: #ffffff;
}
:deep(.dm-select-popper .el-select-dropdown__item) {
  border-radius: 6px;
  color: #6b7280 !important;
}
:deep(.dm-select-popper .el-select-dropdown__item:hover) {
  background-color: #e5e7eb !important;
  color: #111827 !important;
}
:deep(.dm-select-popper .el-select-dropdown__item.is-selected) {
  color: #6b7280 !important;
  background-color: #f3f4f6 !important;
  font-weight: 500;
}
:deep(.dm-select-popper .el-select-dropdown__item.is-hovering) {
  background-color: #e5e7eb !important;
  color: #111827 !important;
}

/* 辅助类 */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>

<style>
/* 自定义MessageBox样式 */
.custom-message-box {
  border-radius: 12px !important;
  padding: 24px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.custom-message-box .el-message-box__header {
  padding-bottom: 16px !important;
}

.custom-message-box .el-message-box__title {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #111827 !important;
}

.custom-message-box .el-message-box__content {
  padding: 16px 0 !important;
  color: #6b7280 !important;
  font-size: 14px !important;
}

.custom-message-box .el-message-box__btns {
  padding-top: 20px !important;
  display: flex !important;
  gap: 12px !important;
  justify-content: flex-end !important;
}

.custom-cancel-button {
  height: 40px !important;
  padding: 0 20px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  background-color: #ffffff !important;
  color: #374151 !important;
  border: 1px solid #d1d5db !important;
  transition: all 0.2s !important;
}

.custom-cancel-button:hover {
  background-color: #f9fafb !important;
  border-color: #9ca3af !important;
}

.custom-confirm-button {
  height: 40px !important;
  padding: 0 20px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  background-color: #dc2626 !important;
  color: #ffffff !important;
  border: none !important;
  transition: all 0.2s !important;
}

.custom-confirm-button:hover {
  background-color: #b91c1c !important;
}
</style>
